import React from 'react';
import PropTypes from 'prop-types';

import {
  LABOR_CATEGORY_ID,
  OVERHEAD_CATEGORY_ID,
} from '../../../../helpers/costcodeHelpers';
import { isNullOrUndefined } from '../../../../helpers/helpers';
import {
  getFormattedHoursLabel,
  getActualCostForCostCode,
  getActualSpendForCategory,
} from '../../../ProjectProgress/progressHelpers';
import ProgressRow from '../../../ProjectProgress/ProgressRow';
import {
  PROGRESS_ROW_TYPE_ACTIVITY,
  PROGRESS_ROW_TYPE_HOURS,
  PROGRESS_ROW_TYPE_SPEND,
  PROGRESS_ROW_TYPE_LABOR_COST,
} from '../../../ProjectProgress/progressConstants';

/* eslint-disable react/jsx-props-no-spreading */
export default function CostTrackingBudgetProgressModal({
  record,
  projectCostcodeDetailsMap,
  userMap,
  progresses,
  averageWage = 0,
  categoriesIdMap,
}) {
  const costcodeId = record?.id;
  const phaseId = record?.phaseId;
  const projectId = record?.projectId;
  const activeCategoryId = record?.activeCategoryId;
  const categoryAssignment = record?.categoryAssignment ?? {};
  const recordForRow = {
    ...record,
    costcodeId,
    phaseId,
    projectId,
    activeCategoryId,
  };
  const { estimatedCost, hours: estimatedHoursRaw = 0 } = categoryAssignment;
  const estimatedHours = estimatedHoursRaw ?? 0;
  const estimatedLaborCost = estimatedHours * averageWage;
  let estimatedCostToUse = estimatedCost;
  if (activeCategoryId === LABOR_CATEGORY_ID && isNullOrUndefined(estimatedCostToUse)) {
    estimatedCostToUse = estimatedLaborCost;
  }
  const clockable = (
    activeCategoryId === LABOR_CATEGORY_ID
    || activeCategoryId === OVERHEAD_CATEGORY_ID
    || categoriesIdMap[activeCategoryId]?.allowTimeTracking
  );
  const actualHoursLabel = clockable ? getFormattedHoursLabel({
    costcodeDetailsMap: projectCostcodeDetailsMap,
    costcodeId,
    phaseId,
  }) : null;

  const actualLaborCost = clockable ? getActualCostForCostCode({
    costcodeDetailsMap: projectCostcodeDetailsMap,
    costcodeId,
    phaseId,
    userMap,
  }) : 0;

  const isCustomCategory = typeof activeCategoryId === 'string' && activeCategoryId.length > 1;

  const actualSpend = getActualSpendForCategory({
    costcodeId,
    phaseId,
    projectId,
    progresses,
    defaultCategoryId: isCustomCategory ? null : activeCategoryId,
    customCategoryId: isCustomCategory ? activeCategoryId : null,
  });

  const labelStyle = {
    fontFamily: 'open-sans-600',
    fontSize: 16,
    alignSelf: 'flex-start',
  };

  const type = categoriesIdMap[activeCategoryId]?.name ?? 'Labor';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {!!clockable && (
        <>
          <span style={labelStyle}>Hours Used ({actualHoursLabel}):</span>
          <ProgressRow
            {...recordForRow}
            type={PROGRESS_ROW_TYPE_HOURS}
            estimatedCost={estimatedCostToUse}
            hours={estimatedHours}
            displayMode
            isStacked
            isModal
          />
        </>
      )}
      <span style={labelStyle}>Actual Spend (${actualSpend.toFixed(2)}):</span>
      <ProgressRow
        {...recordForRow}
        type={PROGRESS_ROW_TYPE_SPEND}
        category={type}
        estimatedLaborCost={estimatedLaborCost}
        estimatedCost={estimatedCostToUse}
        hours={estimatedHours}
        displayMode
        isStacked
        isModal
      />
      <span style={labelStyle}>Total Cost (${(actualLaborCost + actualSpend).toFixed(2)}):</span>
      <ProgressRow
        {...recordForRow}
        estimatedLaborCost={estimatedLaborCost}
        actualLaborCost={actualLaborCost}
        type={PROGRESS_ROW_TYPE_LABOR_COST}
        category={type}
        estimatedCost={estimatedCostToUse}
        hours={estimatedHours}
        isStacked
        displayMode
        isModal
      />
      <span style={labelStyle}>Activity Progress:</span>
      <ProgressRow
        {...recordForRow}
        type={PROGRESS_ROW_TYPE_ACTIVITY}
        displayMode
        hasWrite={false}
        isStacked
        isModal
      />
    </div>
  );
}

CostTrackingBudgetProgressModal.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    phaseId: PropTypes.string,
    projectId: PropTypes.string,
    activeCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    categoryAssignment: PropTypes.shape({
      hours: PropTypes.number,
      estimatedCost: PropTypes.number,
    }),
  }).isRequired,
  projectCostcodeDetailsMap: PropTypes.shape({}),
  userMap: PropTypes.shape({}),
  progresses: PropTypes.shape({}),
  averageWage: PropTypes.number,
  categoriesIdMap: PropTypes.shape({}),
};

CostTrackingBudgetProgressModal.defaultProps = {
  projectCostcodeDetailsMap: {},
  userMap: {},
  progresses: {},
  averageWage: 0,
  categoriesIdMap: {},
};
